// src/components/PDF/FillablePDF.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Upload, 
  FileText, 
  Download, 
  Loader2, 
  Trash2, 
  Layout, 
  Settings, 
  AlertCircle, 
  ArrowLeft,
  Type,
  CheckSquare,
  Calendar,
  Pen,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Move,
  GripHorizontal,
  PanelRightClose,
  PanelRightOpen,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { pdfService } from '../../services/pdfService';
import { useAuthGuard } from '../../hooks/useAuthGuard';

// Field type definitions
const FIELD_TYPES = {
  TEXT: 'text',
  CHECKBOX: 'checkbox',
  DATE: 'date',
  SIGNATURE: 'signature'
};

const FillablePDF = () => {
  const { requireAuth, AuthModal } = useAuthGuard();
  // State management
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState({
    status: 'idle',
    progress: 0,
    message: ''
  });
  const [isExporting, setIsExporting] = useState(false);
  const [interaction, setInteraction] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [showProperties, setShowProperties] = useState(false); // Collapsed by default
  const [isMobilePropertiesOpen, setIsMobilePropertiesOpen] = useState(false);

  // Refs
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    if (uploadedFile.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    setError(null);
    setFile(uploadedFile);
    setProcessing({ status: 'loading', progress: 10, message: 'Processing your PDF document...' });

    try {
      const pageImages = await pdfService.loadPdfAsImages(uploadedFile);
      if (pageImages.length === 0) throw new Error('PDF contains no readable pages.');

      setPages(pageImages);
      setProcessing({ status: 'editing', progress: 100, message: '' });
      setCurrentPageIndex(0);
      toast.success(`Loaded ${pageImages.length} page${pageImages.length > 1 ? 's' : ''}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load PDF.';
      setError(msg);
      toast.error(msg);
      setProcessing({ status: 'idle', progress: 0, message: '' });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Add a new field
  const addField = (type) => {
    const currentPage = pages[currentPageIndex];
    if (!currentPage) return;

    // Default sizes based on field type (as percentage of page)
    const fieldSizes = {
      text: { width: 0.25, height: 0.035 },
      checkbox: { width: 0.025, height: 0.025 },
      date: { width: 0.18, height: 0.035 },
      signature: { width: 0.3, height: 0.08 }
    };

    const size = fieldSizes[type] || fieldSizes.text;

    const newField = {
      id: `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      label: `${type.charAt(0).toUpperCase() + type.slice(1)} ${currentPage.fields.length + 1}`,
      x: 0.1,
      y: 0.1,
      width: size.width,
      height: size.height,
      required: false,
      pageIndex: currentPageIndex
    };

    const updatedPages = [...pages];
    updatedPages[currentPageIndex].fields.push(newField);
    setPages(updatedPages);
    setSelectedFieldId(newField.id);
    setShowProperties(true); // Open properties when adding a field
    toast.success(`Added ${type} field`);
  };

  // Update field properties
  const updateField = useCallback((id, updates) => {
    setPages(prev => {
      const updated = [...prev];
      const page = { ...updated[currentPageIndex] };
      page.fields = page.fields.map(f => f.id === id ? { ...f, ...updates } : f);
      updated[currentPageIndex] = page;
      return updated;
    });
  }, [currentPageIndex]);

  // Delete a field
  const deleteField = (id) => {
    const updatedPages = [...pages];
    updatedPages[currentPageIndex].fields = updatedPages[currentPageIndex].fields.filter(f => f.id !== id);
    setPages(updatedPages);
    if (selectedFieldId === id) setSelectedFieldId(null);
    toast.success('Field deleted');
  };

  // Mouse/Touch interaction handlers - FIXED coordinate calculation
  const onInteractionStart = (e, field, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setInteraction({
      fieldId: field.id,
      type,
      startX: clientX,
      startY: clientY,
      startFieldX: field.x,
      startFieldY: field.y,
      startFieldW: field.width,
      startFieldH: field.height
    });
    setSelectedFieldId(field.id);
  };

  // FIXED: Calculate delta based on actual canvas dimensions
  const onInteractionMove = useCallback((e) => {
    if (!interaction || !canvasRef.current) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Get the actual rendered dimensions of the canvas
    const rect = canvasRef.current.getBoundingClientRect();
    
    // Calculate delta as percentage of actual displayed size
    const deltaX = (clientX - interaction.startX) / rect.width;
    const deltaY = (clientY - interaction.startY) / rect.height;

    if (interaction.type === 'move') {
      const newX = Math.max(0, Math.min(1 - interaction.startFieldW, interaction.startFieldX + deltaX));
      const newY = Math.max(0, Math.min(1 - interaction.startFieldH, interaction.startFieldY + deltaY));
      updateField(interaction.fieldId, { x: newX, y: newY });
    } else if (interaction.type === 'resize') {
      const newW = Math.max(0.02, Math.min(1 - interaction.startFieldX, interaction.startFieldW + deltaX));
      const newH = Math.max(0.02, Math.min(1 - interaction.startFieldY, interaction.startFieldH + deltaY));
      updateField(interaction.fieldId, { width: newW, height: newH });
    }
  }, [interaction, updateField]);

  const onInteractionEnd = useCallback(() => {
    setInteraction(null);
  }, []);

  // Event listeners for drag/resize
  useEffect(() => {
    if (interaction) {
      window.addEventListener('mousemove', onInteractionMove);
      window.addEventListener('mouseup', onInteractionEnd);
      window.addEventListener('touchmove', onInteractionMove, { passive: false });
      window.addEventListener('touchend', onInteractionEnd);
    }
    return () => {
      window.removeEventListener('mousemove', onInteractionMove);
      window.removeEventListener('mouseup', onInteractionEnd);
      window.removeEventListener('touchmove', onInteractionMove);
      window.removeEventListener('touchend', onInteractionEnd);
    };
  }, [interaction, onInteractionMove, onInteractionEnd]);

  // Export fillable PDF
  const handleExport = () => {
    if (!file) return;

    const totalFields = pages.reduce((sum, page) => sum + page.fields.length, 0);
    if (totalFields === 0) {
      toast.error('Please add at least one form field');
      return;
    }

    requireAuth(async () => {
      setIsExporting(true);
      setProcessing({ status: 'exporting', progress: 50, message: 'Creating fillable PDF...' });

      try {
        const pdfBytes = await pdfService.exportFillablePdf(file, pages);
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Fillable_${file.name}`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Fillable PDF downloaded successfully!');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to export PDF';
        setError(msg);
        toast.error(msg);
      } finally {
        setIsExporting(false);
        setProcessing({ status: 'editing', progress: 100, message: '' });
      }
    });
  };

  // Reset to initial state
  const reset = () => {
    setFile(null);
    setPages([]);
    setError(null);
    setCurrentPageIndex(0);
    setSelectedFieldId(null);
    setProcessing({ status: 'idle', progress: 0, message: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Zoom controls
  const handleZoom = (delta) => {
    setZoom(prev => Math.max(0.5, Math.min(2, prev + delta)));
  };

  // Current page data
  const currentPage = pages[currentPageIndex];
  const selectedField = currentPage?.fields.find(f => f.id === selectedFieldId);

  // RENDER: Idle/Upload State
  if (processing.status === 'idle') {
    return (
      <div className="flex flex-col items-center justify-center py-8 md:py-12">
        <div className="max-w-xl w-full bg-white rounded-2xl shadow-lg p-6 md:p-10 text-center border border-gray-100">
          <div className="w-16 h-16 md:w-20 md:h-20 bg-[#232751] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Layout className="w-8 h-8 md:w-10 md:h-10 text-white" />
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Fillable PDF Creator
          </h2>
          <p className="text-gray-500 text-sm md:text-base mb-8 leading-relaxed">
            Upload a static PDF and transform it into an interactive fillable form with text fields, checkboxes, and more.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 text-red-700 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-left">{error}</p>
            </div>
          )}

          <label className="group relative flex flex-col items-center justify-center w-full h-44 md:h-52 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#232751]/70 hover:bg-[#232751]/5 transition-all duration-300">
            <div className="flex flex-col items-center justify-center py-6">
              <Upload className="w-10 h-10 md:w-12 md:h-12 text-gray-300 group-hover:text-[#232751] transition-colors mb-4" />
              <p className="text-sm md:text-base text-gray-600 font-medium">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-400 mt-1">PDF documents only (max 50MB)</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,application/pdf"
              onChange={handleFileUpload}
            />
          </label>

          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-3">Supported field types:</p>
            <div className="flex justify-center gap-4 flex-wrap">
              {[
                { icon: Type, label: 'Text' },
                { icon: CheckSquare, label: 'Checkbox' },
                { icon: Calendar, label: 'Date' },
                { icon: Pen, label: 'Signature' }
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-1.5 text-gray-500 text-xs">
                  <Icon className="w-3.5 h-3.5" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // RENDER: Loading State
  if (processing.status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative mb-6">
          <div className="w-16 h-16 md:w-20 md:h-20 border-4 border-[#232751]/15 border-t-[#232751] rounded-full animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <FileText className="w-6 h-6 md:w-8 md:h-8 text-[#232751]" />
          </div>
        </div>
        <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">Loading PDF...</h3>
        <p className="text-gray-500 text-sm">{processing.message}</p>
      </div>
    );
  }

  // RENDER: Editor State
  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)] min-h-[600px] bg-gray-50 -mx-8 -my-8 rounded-xl overflow-hidden">
      {/* Sidebar - Page Thumbnails (Desktop) */}
      <div className="hidden lg:flex w-48 bg-white border-r border-gray-200 flex-col flex-shrink-0">
        <div className="p-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-[#232751] p-1.5 rounded-lg">
              <Layout className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-semibold text-gray-800 text-xs">Form Editor</span>
          </div>
          <button
            onClick={reset}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
            title="Start over"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider px-1">
            Pages ({pages.length})
          </p>
          {pages.map((p, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPageIndex(idx)}
              className={`w-full relative group transition-all rounded-lg overflow-hidden border-2 text-left ${
                currentPageIndex === idx
                  ? 'border-[#232751] ring-2 ring-[#232751]/10'
                  : 'border-gray-100 hover:border-[#232751]/20'
              }`}
            >
              <img
                src={p.image}
                alt={`Page ${idx + 1}`}
                className="w-full h-auto opacity-90 group-hover:opacity-100 transition-opacity"
              />
              <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[8px] px-1 py-0.5 rounded backdrop-blur-sm font-medium">
                {idx + 1}
              </div>
              {p.fields.length > 0 && (
                <div className="absolute top-1 right-1 bg-[#232751] text-white text-[8px] w-4 h-4 flex items-center justify-center rounded-full font-medium">
                  {p.fields.length}
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="p-2 border-t border-gray-100">
          <button
            disabled={isExporting}
            onClick={handleExport}
            className="w-full bg-[#232751] hover:bg-[#1b1f43] disabled:bg-[#232751]/40 text-white py-2 rounded-lg flex items-center justify-center gap-1.5 font-semibold shadow-lg shadow-[#232751]/10 transition-all text-xs"
          >
            {isExporting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            Download
          </button>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center justify-between gap-2">
          {/* Mobile back button */}
          <button
            onClick={reset}
            className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>

          {/* Page info & navigation (mobile) */}
          <div className="flex items-center gap-1 lg:hidden">
            <button
              onClick={() => setCurrentPageIndex(prev => Math.max(0, prev - 1))}
              disabled={currentPageIndex === 0}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-medium text-gray-700 min-w-[40px] text-center">
              {currentPageIndex + 1}/{pages.length}
            </span>
            <button
              onClick={() => setCurrentPageIndex(prev => Math.min(pages.length - 1, prev + 1))}
              disabled={currentPageIndex === pages.length - 1}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Add field buttons */}
          <div className="flex items-center gap-1 overflow-x-auto flex-1 justify-center">
            <button
              onClick={() => addField(FIELD_TYPES.TEXT)}
              className="flex items-center gap-1 px-2 py-1.5 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-lg border border-gray-200 transition-colors whitespace-nowrap"
            >
              <Type className="w-3.5 h-3.5 text-[#232751]" />
              <span className="hidden sm:inline">Text</span>
            </button>
            <button
              onClick={() => addField(FIELD_TYPES.CHECKBOX)}
              className="flex items-center gap-1 px-2 py-1.5 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-lg border border-gray-200 transition-colors whitespace-nowrap"
            >
              <CheckSquare className="w-3.5 h-3.5 text-[#232751]" />
              <span className="hidden sm:inline">Check</span>
            </button>
            <button
              onClick={() => addField(FIELD_TYPES.DATE)}
              className="flex items-center gap-1 px-2 py-1.5 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-lg border border-gray-200 transition-colors whitespace-nowrap"
            >
              <Calendar className="w-3.5 h-3.5 text-[#232751]" />
              <span className="hidden sm:inline">Date</span>
            </button>
            <button
              onClick={() => addField(FIELD_TYPES.SIGNATURE)}
              className="flex items-center gap-1 px-2 py-1.5 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-lg border border-gray-200 transition-colors whitespace-nowrap"
            >
              <Pen className="w-3.5 h-3.5 text-[#232751]" />
              <span className="hidden sm:inline">Sign</span>
            </button>
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-1">
            {/* Zoom controls */}
            <div className="hidden md:flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => handleZoom(-0.25)}
                className="p-1 hover:bg-white rounded transition-colors"
              >
                <ZoomOut className="w-3.5 h-3.5 text-gray-600" />
              </button>
              <span className="text-[10px] font-medium text-gray-600 w-10 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => handleZoom(0.25)}
                className="p-1 hover:bg-white rounded transition-colors"
              >
                <ZoomIn className="w-3.5 h-3.5 text-gray-600" />
              </button>
            </div>

            {/* Desktop: Properties toggle */}
            <button
              onClick={() => setShowProperties(!showProperties)}
              className={`hidden lg:flex p-1.5 rounded-lg transition-colors ${
                showProperties ? 'bg-[#232751]/10 text-[#232751]' : 'hover:bg-gray-100 text-gray-600'
              }`}
              title={showProperties ? 'Hide Properties' : 'Show Properties'}
            >
              {showProperties ? (
                <PanelRightClose className="w-4 h-4" />
              ) : (
                <PanelRightOpen className="w-4 h-4" />
              )}
            </button>

            {/* Mobile: Properties toggle */}
            <button
              onClick={() => setIsMobilePropertiesOpen(true)}
              className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg"
            >
              <Settings className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Canvas Area - FIXED: Exact A4 aspect ratio maintained */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-auto p-4 md:p-6 flex justify-center items-start bg-gray-100/50"
        >
          {currentPage && (
            <div
              ref={canvasRef}
              className="relative shadow-2xl bg-white border border-gray-200 select-none flex-shrink-0"
              style={{
                // Use exact PDF dimensions scaled by zoom
                width: currentPage.width * zoom,
                height: currentPage.height * zoom,
              }}
              onClick={() => setSelectedFieldId(null)}
            >
              {/* PDF Page Image - must fill container exactly */}
              <img
                src={currentPage.image}
                alt={`Page ${currentPageIndex + 1}`}
                className="absolute inset-0 w-full h-full object-fill pointer-events-none"
                draggable={false}
              />

              {/* Field Overlays - positioned as percentage of container */}
              {currentPage.fields.map(field => (
                <div
                  key={field.id}
                  onMouseDown={(e) => onInteractionStart(e, field, 'move')}
                  onTouchStart={(e) => onInteractionStart(e, field, 'move')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFieldId(field.id);
                    setShowProperties(true);
                  }}
                  className={`absolute border-2 transition-all cursor-move group touch-none ${
                    field.type === 'checkbox' ? 'rounded' : 'rounded-sm'
                  } ${
                    selectedFieldId === field.id || interaction?.fieldId === field.id
                      ? 'border-[#232751] bg-[#232751]/20 ring-2 ring-[#232751]/30 z-20'
                      : 'border-[#232751]/60 bg-[#232751]/10 hover:border-[#232751] hover:bg-[#232751]/20 z-10'
                  }`}
                  style={{
                    left: `${field.x * 100}%`,
                    top: `${field.y * 100}%`,
                    width: `${field.width * 100}%`,
                    height: `${field.height * 100}%`
                  }}
                >
                  {/* Field label tooltip */}
                  <div className="absolute -top-5 left-0 bg-[#232751] text-white text-[8px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {field.label}
                  </div>

                  {/* Move indicator */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <Move className="w-3 h-3 text-[#232751]/50" />
                  </div>

                  {/* Delete button */}
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteField(field.id);
                    }}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white p-0.5 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>

                  {/* Resize handle */}
                  <div
                    onMouseDown={(e) => onInteractionStart(e, field, 'resize')}
                    onTouchStart={(e) => onInteractionStart(e, field, 'resize')}
                    className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize flex items-center justify-center"
                  >
                    <GripHorizontal className="w-2.5 h-2.5 text-[#232751] opacity-0 group-hover:opacity-100 rotate-45" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mobile: Download button */}
        <div className="lg:hidden p-2 bg-white border-t border-gray-200">
          <button
            disabled={isExporting}
            onClick={handleExport}
            className="w-full bg-[#232751] hover:bg-[#1b1f43] disabled:bg-[#232751]/40 text-white py-2.5 rounded-lg flex items-center justify-center gap-2 font-semibold text-sm"
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download Fillable PDF
          </button>
        </div>
      </div>

      {/* Properties Panel (Desktop) - Toggleable */}
      {showProperties && (
        <div className="hidden lg:block w-64 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0">
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-400" />
              <h3 className="font-semibold text-gray-800 text-xs">Field Properties</h3>
            </div>
            <button
              onClick={() => setShowProperties(false)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>

          <div className="p-3">
            {selectedField ? (
              <FieldPropertyEditor
                field={selectedField}
                onUpdate={(updates) => updateField(selectedField.id, updates)}
                onDelete={() => deleteField(selectedField.id)}
              />
            ) : currentPage?.fields.length > 0 ? (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  All Fields ({currentPage.fields.length})
                </p>
                {currentPage.fields.map((field) => (
                  <FieldListItem
                    key={field.id}
                    field={field}
                    isSelected={selectedFieldId === field.id}
                    onSelect={() => setSelectedFieldId(field.id)}
                    onDelete={() => deleteField(field.id)}
                  />
                ))}
              </div>
            ) : (
              <EmptyPropertiesState />
            )}
          </div>
        </div>
      )}

      {/* Mobile Properties Panel (Slide-over) */}
      {isMobilePropertiesOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobilePropertiesOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white shadow-xl flex flex-col">
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-gray-400" />
                <h3 className="font-semibold text-gray-800 text-sm">Field Properties</h3>
              </div>
              <button
                onClick={() => setIsMobilePropertiesOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {selectedField ? (
                <FieldPropertyEditor
                  field={selectedField}
                  onUpdate={(updates) => updateField(selectedField.id, updates)}
                  onDelete={() => deleteField(selectedField.id)}
                />
              ) : currentPage?.fields.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    All Fields ({currentPage.fields.length})
                  </p>
                  {currentPage.fields.map((field) => (
                    <FieldListItem
                      key={field.id}
                      field={field}
                      isSelected={selectedFieldId === field.id}
                      onSelect={() => setSelectedFieldId(field.id)}
                      onDelete={() => deleteField(field.id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyPropertiesState />
              )}
            </div>
          </div>
        </div>
      )}

      {AuthModal}

      {/* Exporting Overlay */}
      {processing.status === 'exporting' && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4 max-w-xs w-full mx-4">
            <div className="relative">
              <div className="w-14 h-14 border-4 border-[#232751]/15 border-t-[#232751] rounded-full animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-2.5 h-2.5 bg-[#232751] rounded-full animate-pulse" />
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-base font-semibold text-gray-900 mb-1">Creating Fillable PDF</h3>
              <p className="text-gray-500 text-xs">
                Embedding form fields...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Field Property Editor Component (for selected field)
const FieldPropertyEditor = ({ field, onUpdate, onDelete }) => {
  const getFieldIcon = (type) => {
    switch (type) {
      case 'checkbox': return CheckSquare;
      case 'date': return Calendar;
      case 'signature': return Pen;
      default: return Type;
    }
  };

  const Icon = getFieldIcon(field.type);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-[#232751]/10">
            <Icon className="w-4 h-4 text-[#232751]" />
          </div>
          <span className="text-sm font-medium text-gray-700 capitalize">{field.type} Field</span>
        </div>
        <button
          onClick={onDelete}
          className="p-1 hover:bg-red-100 rounded transition-colors"
        >
          <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
        </button>
      </div>

      <div>
        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
          Label / Name
        </label>
        <input
          type="text"
          value={field.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          className="w-full px-2.5 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#232751]/20 focus:border-[#232751] outline-none"
        />
      </div>

      <div>
        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
          Field Type
        </label>
        <select
          value={field.type}
          onChange={(e) => onUpdate({ type: e.target.value })}
          className="w-full px-2.5 py-2 bg-white border border-gray-200 rounded-lg text-sm outline-none"
        >
          <option value="text">Text Input</option>
          <option value="checkbox">Checkbox</option>
          <option value="date">Date Field</option>
          <option value="signature">Signature</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            X Position
          </label>
          <div className="text-xs text-gray-600 font-mono bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2">
            {(field.x * 100).toFixed(1)}%
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            Y Position
          </label>
          <div className="text-xs text-gray-600 font-mono bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2">
            {(field.y * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            Width
          </label>
          <div className="text-xs text-gray-600 font-mono bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2">
            {(field.width * 100).toFixed(1)}%
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            Height
          </label>
          <div className="text-xs text-gray-600 font-mono bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2">
            {(field.height * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      <label className="flex items-center gap-2 pt-2 cursor-pointer">
        <input
          type="checkbox"
          checked={field.required}
          onChange={(e) => onUpdate({ required: e.target.checked })}
          className="w-4 h-4 text-[#232751] rounded focus:ring-[#232751] border-gray-300"
        />
        <span className="text-xs font-medium text-gray-600">Required field</span>
      </label>
    </div>
  );
};

// Field List Item Component (for unselected state)
const FieldListItem = ({ field, isSelected, onSelect, onDelete }) => {
  const getFieldIcon = (type) => {
    switch (type) {
      case 'checkbox': return CheckSquare;
      case 'date': return Calendar;
      case 'signature': return Pen;
      default: return Type;
    }
  };

  const Icon = getFieldIcon(field.type);

  return (
    <div
      onClick={onSelect}
      className={`p-2.5 rounded-lg border transition-all cursor-pointer flex items-center justify-between ${
        isSelected
          ? 'bg-[#232751]/5 border-[#232751]/20'
          : 'bg-gray-50 border-gray-200 hover:border-[#232751]/20'
      }`}
    >
      <div className="flex items-center gap-2">
        <div className={`p-1 rounded ${isSelected ? 'bg-[#232751]/10' : 'bg-gray-100'}`}>
          <Icon className={`w-3 h-3 ${isSelected ? 'text-[#232751]' : 'text-gray-500'}`} />
        </div>
        <span className="text-xs font-medium text-gray-700 truncate max-w-[120px]">
          {field.label}
        </span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="p-1 hover:bg-red-100 rounded transition-colors opacity-0 group-hover:opacity-100"
      >
        <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
      </button>
    </div>
  );
};

// Empty Properties State Component
const EmptyPropertiesState = () => (
  <div className="flex flex-col items-center justify-center h-40 text-center">
    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-3">
      <FileText className="w-5 h-5 text-gray-300" />
    </div>
    <p className="text-xs text-gray-500 font-medium mb-1">No Fields Yet</p>
    <p className="text-[10px] text-gray-400 leading-relaxed max-w-[160px]">
      Add fields using the toolbar, then click to select and edit.
    </p>
  </div>
);

export default FillablePDF;
